/**
 * Contrato da folha de filtros e da página estreita de `/revisao`.
 *
 * As quatro dimensões de seleção — matéria, palavra-chave, baralho e modo de
 * estudo — são as mesmas do trilho largo (`REVISAO_RAIL` sem a agenda). A
 * agenda fica na página, em fita. A folha edita um rascunho; aplicar grava
 * esse rascunho na mesma store que o largo já lê, para a sessão ser a mesma
 * nas duas larguras.
 *
 * openspec/changes/flashcards-revisao-redesign/specs/revisao-home/spec.md
 * Requirement: No viewport estreito os filtros vivem numa folha
 * design.md Decisão 8 — o corte é 768px, o de `useIsMobile`.
 */
import type { QueueOrder } from "../../../../core/store/useRevisaoSelecaoStore";
import type { FichaDeFiltro } from "../barraDeInicioContrato";
import type { AgendaDia, RevisaoDiaLeitura } from "../home/revisaoHome";

export const REVISAO_FILTER_SHEET_TABS = ["materia", "palavras", "baralhos", "modo"] as const;

export type RevisaoFilterSheetTab = (typeof REVISAO_FILTER_SHEET_TABS)[number];

export const REVISAO_FILTER_SHEET_TAB_LABEL: Record<RevisaoFilterSheetTab, string> = {
  materia: "Matéria",
  palavras: "Palavras-chave",
  baralhos: "Baralhos",
  modo: "Modo de estudo",
};

export interface RevisaoFilterSheetOpcao {
  value: string;
  label: string;
}

export interface RevisaoBaralhoOpcao {
  name: string;
  count: number;
}

/**
 * A matéria option for the sheet — id separate from display label, like
 * `RevisaoFilterSheetOpcao`, plus the flashcard count the facets inventory
 * carries per subject (openspec change revisao-verso-painel-e-autoria-de-imagem,
 * design.md Decisão 6).
 */
export interface RevisaoMateriaOpcao {
  value: string;
  label: string;
  count: number;
}

/**
 * A palavra-chave option carrying its flashcard count — same shape as
 * `RevisaoBaralhoOpcao`, named separately because a keyword isn't a deck.
 */
export interface RevisaoPalavraOpcao {
  name: string;
  count: number;
}

/**
 * Folha inferior. Não busca e não lê a store: o pai passa o rascunho e a
 * contagem que esse rascunho produz.
 */
export interface RevisaoFilterSheetProps {
  open: boolean;
  onClose: () => void;
  aba: RevisaoFilterSheetTab;
  onAba: (aba: RevisaoFilterSheetTab) => void;

  materias: RevisaoMateriaOpcao[];
  materiasSelecionadas: string[];
  onToggleMateria: (id: string) => void;

  palavras: RevisaoPalavraOpcao[];
  palavrasSelecionadas: string[];
  onTogglePalavra: (value: string) => void;

  baralhos: RevisaoBaralhoOpcao[];
  baralhosSelecionados: string[];
  onToggleBaralho: (name: string) => void;

  ahead: boolean;
  onAhead: (ahead: boolean) => void;
  limit: number;
  onLimit: (limit: number) => void;
  queueOrder: QueueOrder;
  onQueueOrder: (queueOrder: QueueOrder) => void;

  /**
   * Cartões que o rascunho da folha produz. Ausente enquanto a contagem não
   * chegou — o botão não inventa um zero no lugar de "ainda não sei".
   */
  resultCount?: number;
  applying?: boolean;
  onApply: () => void;
  /** Descarta o rascunho da folha (as quatro dimensões), sem fechar. */
  onClear: () => void;

  /**
   * Set while a fixed day overrides Matéria — mirrors `DimensaoSecao`'s own
   * `desabilitada` prop (openspec change revisao-verso-painel-e-autoria-de-imagem,
   * design.md Decisão 5). Every chip in the aba renders disabled and the
   * motivo renders above the options, tied by `aria-describedby`. No option
   * is removed from the list.
   */
  materiaDesabilitada?: { motivo: string };
  /** Same as `materiaDesabilitada`, for the Palavras-chave aba. */
  palavrasDesabilitada?: { motivo: string };
  /** Same as `materiaDesabilitada`, for the Baralhos aba. */
  baralhosDesabilitada?: { motivo: string };
  /**
   * The fixed day's own baralhos (may be empty). Mirrors `DimensaoSecao`'s
   * `fixadas` prop: rendered chosen and non-actionable; because the day, not
   * the manual pick, decides the fila, no other option in the aba stays
   * actionable either — even without `baralhosDesabilitada` set.
   */
  baralhosFixados?: string[];
}

/** Página de `/revisao` abaixo de 768px. A folha chega pronta em `folha`. */
export interface RevisaoHomeEstreitoProps {
  leitura: RevisaoDiaLeitura;
  /** A primeira leitura ainda não chegou. Zero no herói não é "nada a estudar". */
  aguardando?: boolean;
  fichas: FichaDeFiltro[];
  onRemoverFicha: (ficha: FichaDeFiltro) => void;
  onLimpar: () => void;
  podeLimpar: boolean;
  /** Antecipar já aplicado — o da barra de início, não o rascunho da folha. */
  ahead: boolean;
  onAhead: (ahead: boolean) => void;
  onIniciar: () => void;
  agenda: AgendaDia[];
  diaFixado: string | null;
  onFixarDia: (iso: string | null) => void;
  onPraticarDia: (iso: string) => void;
  /** Lista da página. Escolher um baralho continua na aba da folha. */
  baralhos: RevisaoBaralhoOpcao[];
  baralhosSelecionados: string[];
  /** Quantos filtros a página já mostra. O botão lê "Filtros · N". */
  filtrosEscolhidos: number;
  onAbrirFolha: () => void;
  folha: RevisaoFilterSheetProps;
  /**
   * A casca já reserva a App Bar (4rem) e a BottomTabBar (4.5rem), e o
   * conteúdo do layout tem 1.5rem de padding inferior. A história não tem
   * casca, então não passa isto.
   */
  reservaDaBarraDeAbas?: boolean;
}

const formatoContagem = new Intl.NumberFormat("pt-BR");

/** "Filtros · 3" — o comando que abre a folha. */
export function rotuloFiltrosEscolhidos(quantidade: number): string {
  return `Filtros · ${formatoContagem.format(Math.max(0, quantidade))}`;
}

/**
 * "Aplicar · 106 cartões". Sem contagem, só "Aplicar" — nunca "Aplicar · 0"
 * enquanto o número ainda não chegou.
 */
export function rotuloAplicarFolha(resultCount: number | undefined): string {
  if (resultCount === undefined) return "Aplicar";
  const quantidade = formatoContagem.format(resultCount);
  const substantivo = resultCount === 1 ? "cartão" : "cartões";
  return `Aplicar · ${quantidade} ${substantivo}`;
}
